/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Prompt Templates & Dynamic Forms DTOs (OpenAPI 3.1 Compatible)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Supported field types for dynamic form rendering.
 */
export const PromptFieldTypeSchema = z
  .enum(['text', 'textarea', 'select', 'multiselect', 'number'])
  .openapi({
    description: 'Field rendering widget type in frontend Dynamic Form',
    example: 'text',
  });

export type PromptFieldType = z.infer<typeof PromptFieldTypeSchema>;

/**
 * Option item for select / multiselect fields.
 */
export const PromptFieldOptionSchema = z
  .object({
    label: z.string().openapi({ description: 'Display label', example: 'Professional' }),
    value: z.string().openapi({ description: 'Submitted value', example: 'professional' }),
  })
  .openapi('PromptFieldOption');

export type PromptFieldOption = z.infer<typeof PromptFieldOptionSchema>;

/**
 * Validation rules for a prompt template input field.
 */
export const PromptFieldValidationSchema = z
  .object({
    min: z.number().optional().openapi({ description: 'Minimum number or string length', example: 5 }),
    max: z.number().optional().openapi({ description: 'Maximum number or string length', example: 500 }),
    pattern: z.string().optional().openapi({ description: 'RegEx validation pattern', example: '^[a-zA-Z0-9 ]+$' }),
  })
  .openapi('PromptFieldValidation');

export type PromptFieldValidation = z.infer<typeof PromptFieldValidationSchema>;

/**
 * Single input field definition within a prompt template's dynamic schema.
 */
export const PromptFieldSchema = z
  .object({
    name: z.string().min(1).openapi({ description: 'Variable placeholder name used in {{name}}', example: 'topic' }),
    label: z.string().min(1).openapi({ description: 'Human-readable label for form UI', example: 'Article Topic' }),
    type: PromptFieldTypeSchema,
    placeholder: z.string().optional().openapi({ description: 'Input placeholder hint', example: 'e.g. 10 Benefits of Edge Computing' }),
    description: z.string().optional().openapi({ description: 'Help tooltip or subtitle', example: 'Enter the main subject of your article' }),
    required: z.boolean().default(false).openapi({ description: 'Whether this field must be filled', example: true }),
    options: z
      .array(z.union([PromptFieldOptionSchema, z.string()]))
      .optional()
      .openapi({ description: 'List of choices for select and multiselect fields' }),
    defaultValue: z.any().optional().openapi({ description: 'Default fallback value if empty', example: 'Technology' }),
    validation: PromptFieldValidationSchema.optional(),
  })
  .openapi('PromptField');

export type PromptFieldDto = z.infer<typeof PromptFieldSchema>;

/**
 * Prompt category representation.
 */
export const PromptCategoryDtoSchema = z
  .object({
    id: z.string().openapi({ description: 'Category unique ID', example: 'pcat_marketing' }),
    name: z.string().openapi({ description: 'Category display name', example: 'Marketing & Sales' }),
    slug: z.string().openapi({ description: 'URL-friendly category slug', example: 'marketing' }),
    icon: z.string().nullable().optional().openapi({ description: 'Tabler icon identifier', example: 'megaphone' }),
    sortOrder: z.number().default(0).openapi({ description: 'Display priority ordering', example: 10 }),
  })
  .openapi('PromptCategoryDto');

export type PromptCategoryDto = z.infer<typeof PromptCategoryDtoSchema>;

/**
 * Full Prompt Template Data Transfer Object.
 */
export const PromptTemplateDtoSchema = z
  .object({
    id: z.string().openapi({ description: 'Template unique ID', example: 'ptpl_seo_writer' }),
    categoryId: z.string().openapi({ description: 'Parent category ID', example: 'pcat_writing' }),
    title: z.string().openapi({ description: 'Template title', example: 'SEO Long-Form Article Generator' }),
    slug: z.string().openapi({ description: 'Template URL slug', example: 'seo-long-form-article-generator' }),
    description: z.string().nullable().optional().openapi({
      description: 'Short summary of what this writing tool generates',
      example: 'Generates comprehensive, keyword-optimized articles ready for publishing.',
    }),
    systemPrompt: z.string().openapi({
      description: 'System instruction directing the LLM tone and style',
      example: 'You are an elite SEO content strategist and journalist.',
    }),
    promptTemplate: z.string().openapi({
      description: 'Template body containing {{placeholders}} and conditional tags',
      example: 'Write an in-depth article about {{topic}}. Primary keywords: {{primary_keywords}}.',
    }),
    inputSchema: z.array(PromptFieldSchema).openapi({
      description: 'Dynamic JSON Schema defining input fields for Frontend form generation',
    }),
    defaultModelId: z.string().nullable().optional().openapi({ description: 'Default AI Model ID', example: 'mod_gpt_4o' }),
    isActive: z.boolean().default(true).openapi({ description: 'Whether template is enabled for use', example: true }),
    isPremium: z.boolean().default(false).openapi({ description: 'Whether template requires paid plan', example: false }),
    category: PromptCategoryDtoSchema.optional(),
  })
  .openapi('PromptTemplateDto');

export type PromptTemplateDto = z.infer<typeof PromptTemplateDtoSchema>;

/**
 * Query schema for listing and searching prompt templates.
 */
export const PromptListQuerySchema = z
  .object({
    search: z.string().optional().openapi({ description: 'Keyword search in title or description', example: 'article' }),
    categoryId: z.string().optional().openapi({ description: 'Filter by prompt category ID or slug', example: 'pcat_marketing' }),
    isPremium: z
      .enum(['true', 'false'])
      .optional()
      .transform((val) => (val === undefined ? undefined : val === 'true'))
      .openapi({ description: 'Filter by premium tier requirement', example: 'false' }),
    page: z.coerce.number().int().min(1).default(1).openapi({ description: 'Page number (1-based)', example: 1 }),
    limit: z.coerce.number().int().min(1).max(100).default(20).openapi({ description: 'Items per page', example: 20 }),
  })
  .openapi('PromptListQuery');

export type PromptListQueryDto = z.infer<typeof PromptListQuerySchema>;

/**
 * Request payload for creating custom workspace prompt templates.
 */
export const CreateCustomPromptSchema = z
  .object({
    categoryId: z.string().min(1, 'Category ID is required').openapi({ description: 'Target category ID', example: 'pcat_marketing' }),
    title: z.string().min(3, 'Title must be at least 3 characters').max(120).openapi({ description: 'Template title', example: 'My Custom Cold Email' }),
    slug: z.string().optional().openapi({ description: 'Optional custom slug (auto-generated if omitted)', example: 'my-custom-cold-email' }),
    description: z.string().optional().openapi({ description: 'Description of the template', example: 'Tailored outreach email for SaaS founders' }),
    systemPrompt: z.string().min(5, 'System prompt must be at least 5 characters').openapi({
      description: 'System prompt instructions',
      example: 'You are a direct response copywriter specialized in B2B SaaS.',
    }),
    promptTemplate: z.string().min(5, 'Prompt template must be at least 5 characters').openapi({
      description: 'Template string with {{variable}} placeholders',
      example: 'Write an email to {{prospect_name}} about {{solution}} addressing {{pain_point}}.',
    }),
    inputSchema: z.array(PromptFieldSchema).default([]).openapi({
      description: 'Array of input field definitions for frontend form rendering',
    }),
    defaultModelId: z.string().optional().openapi({ description: 'Default AI Model ID', example: 'mod_gpt_4o' }),
    isPremium: z.boolean().default(false).openapi({ description: 'Premium template flag', example: false }),
  })
  .openapi('CreateCustomPrompt');

export type CreateCustomPromptDto = z.infer<typeof CreateCustomPromptSchema>;
